import { useMutation } from '@apollo/client';
import { DELETE_TALENT_LANGUAGE } from '@gql/talentLanguages';
import { checkPropertiesIsHaveValueWithIncludes } from '@helper/validation.utils';
import { Box, styled, Typography, useTheme } from '@mui/material';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { TalentLanguage, TalentLanguageEntry } from '../types';
import { handleDeleteLanguage } from '../utils/languageUtils';
import EmptyView from './EmptyView';
import LanguageItem from './LanguageItem';

const INCOMPLETE_FIELDS_WITH_CERTIFICATE = ['number', 'url', 'endDate', 'file'];
const INCOMPLETE_FIELDS_WITHOUT_CERTIFICATE = [
    'number', 'url', 'endDate', 'file', 'issuer', 'obtainedResult', 'startDate', 'title', '_id',
];

const ErrorMessageContainer = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.palette.errorRed?.main ? `${theme.palette.errorRed.main}20` : '#FCEEEE',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
}));

interface LanguageListContainerProps {
    talentLanguages: TalentLanguage[];
    onEdit: (item: TalentLanguage) => void;
    onSuccess?: () => void;
    onBack?: () => void;
}

const LanguageListContainer = ({
    talentLanguages,
    onEdit,
    onSuccess,
    onBack,
}: LanguageListContainerProps) => {
    const theme = useTheme();
    const [deleteLanguage] = useMutation(DELETE_TALENT_LANGUAGE);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setHasError(
            talentLanguages.some(({ languages }) =>
                languages.some((entry: TalentLanguageEntry) => {
                    const hasCertificate = !isEmpty(entry.certificates?.[0]);
                    const excludedFields = hasCertificate
                        ? INCOMPLETE_FIELDS_WITH_CERTIFICATE
                        : INCOMPLETE_FIELDS_WITHOUT_CERTIFICATE;
                    return !checkPropertiesIsHaveValueWithIncludes(
                        { ...entry.certificates[0], ...entry.language[0], ...entry.level[0] },
                        excludedFields,
                    );
                }),
            ),
        );
    }, [talentLanguages]);

    return (
        <React.Fragment>
            {hasError && (
                <ErrorMessageContainer>
                    <Typography fontSize={14} sx={{ color: theme.palette.errorRed?.main ?? '#E45858' }}>
                        Veuillez compléter les informations manquantes ou supprimer la formation encadrée de rouge.
                    </Typography>
                </ErrorMessageContainer>
            )}

            {(talentLanguages ?? []).length === 0 && <EmptyView />}

            {(talentLanguages ?? []).map((talentLanguage: TalentLanguage, outerIndex: number) =>
                (talentLanguage.languages ?? []).map((entry: TalentLanguageEntry, index: number) => (
                    <LanguageItem
                        key={talentLanguage._id ?? `${outerIndex}-${index}`}
                        itemLanguage={!isEmpty(entry.language) ? entry.language[0] : null}
                        itemCertificates={!isEmpty(entry.certificates) ? entry.certificates : []}
                        itemLevel={!isEmpty(entry.level) ? entry.level[0] : null}
                        onPressEdit={() => onEdit(talentLanguage)}
                        onPressDelete={() => handleDeleteLanguage(talentLanguage._id ?? '', deleteLanguage)}
                        isDivide={
                            (talentLanguages ?? []).length > 1 &&
                            outerIndex !== (talentLanguages ?? []).length - 1
                        }
                    />
                )),
            )}
        </React.Fragment>
    );
};

export default LanguageListContainer;
